import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppLayout } from "../shared/layout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import {
  ShieldAlert,
  Lightbulb,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  Copy,
  MessageCircle,
  ImagePlus,
  X,
  Share2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  analyzeConversation,
  analyzeConversationImage,
  getFlagAnalyses,
  deleteFlagAnalysis,
  startPracticeSession,
} from "@/data/requests/practice-requests";
import { useTranslation } from "react-i18next";

const RISK_STYLES = {
  green: { labelKey: "flags.risk.green", cls: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100", icon: CheckCircle2 },
  yellow: { labelKey: "flags.risk.yellow", cls: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100", icon: AlertTriangle },
  red: { labelKey: "flags.risk.red", cls: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100", icon: ShieldAlert },
};

const SEVERITY_CLS = {
  low: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  medium: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100",
  high: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100",
};

const RISK_GRADIENT = {
  green: ["#23C088", "#0E6E54"],
  yellow: ["#F0A93B", "#8A4F0B"],
  red: ["#F0623F", "#8E1F14"],
};

// Shareable card — shows NO identity and NO raw messages. Verdict + patterns + disclaimer only.
function RedFlagShareCard({ analysis, t, toast }) {
  const ref = React.useRef(null);
  const [saving, setSaving] = React.useState(false);
  const level = analysis.riskLevel || "yellow";
  const grad = RISK_GRADIENT[level] || RISK_GRADIENT.yellow;
  const patterns = Array.isArray(analysis.patterns) ? analysis.patterns.slice(0, 3) : [];

  const save = async () => {
    setSaving(true);
    try {
      const { default: html2canvas } = await import("html2canvas");
      const canvas = await html2canvas(ref.current, { scale: 2, backgroundColor: null, useCORS: true });
      const link = document.createElement("a");
      link.download = "pairvio-redflag.png";
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (e) {
      toast?.({ title: "Couldn't save", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div ref={ref} style={{ position: "relative", overflow: "hidden", borderRadius: 20, padding: 22, color: "#fff", background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})` }}>
        <div style={{ position: "absolute", top: -60, right: -50, width: 180, height: 180, borderRadius: "50%", background: "rgba(255,255,255,0.12)" }} />
        <div style={{ position: "relative" }}>
          <div className="flex items-center gap-2 mb-4">
            <span className="font-semibold" style={{ fontFamily: '"Fraunces", serif' }}>Pairvio</span>
          </div>
          <p style={{ fontSize: 11, letterSpacing: "0.12em", opacity: 0.85, fontWeight: 700, textTransform: "uppercase" }}>{t("flagsx.shareEyebrow")}</p>
          <p style={{ fontSize: 24, fontWeight: 800, marginTop: 6, marginBottom: 14 }}>{t(`flags.risk.${level}`)}</p>
          {patterns.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {patterns.map((p, i) => (
                <span key={i} style={{ background: "rgba(255,255,255,0.22)", borderRadius: 999, padding: "5px 11px", fontSize: 13, fontWeight: 700 }}>{p.name}</span>
              ))}
            </div>
          )}
          <p style={{ fontSize: 11.5, lineHeight: 1.5, opacity: 0.85, marginBottom: 14 }}>{t("flagsx.shareDisclaimer")}</p>
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.25)", paddingTop: 10, fontSize: 12, fontWeight: 600, opacity: 0.9 }}>pairvio.app</div>
        </div>
      </div>
      <Button variant="outline" className="w-full" onClick={save} disabled={saving}>
        <Share2 className="mr-2 h-4 w-4" /> {t("flagsx.shareButton")}
      </Button>
    </div>
  );
}

function AnalysisCard({ analysis, onDelete, onRehearse, rehearsePending, toast }) {
  const { t } = useTranslation();
  const risk = RISK_STYLES[analysis.riskLevel] || RISK_STYLES.yellow;
  const RiskIcon = risk.icon;
  const patterns = Array.isArray(analysis.patterns) ? analysis.patterns : [];

  return (
    <Card className="rounded-[1.75rem] border-[#F1D8DE] bg-white/95 shadow-[0_14px_32px_rgba(169,35,69,0.07)]">
      <CardHeader>
        <div className="flex items-center justify-between">
          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium ${risk.cls}`}>
            <RiskIcon className="h-4 w-4" /> {t(risk.labelKey)}
          </span>
          {onDelete && (
            <button
              onClick={() => onDelete(analysis.id)}
              className="text-gray-400 hover:text-red-500"
              aria-label={t("flags.history.delete")}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-200">
          {analysis.summary}
        </p>

        {patterns.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">{t("flags.patternsTitle")}</h4>
            {patterns.map((p, i) => (
              <div key={i} className="rounded-2xl border border-[#F2DFE3] bg-[#FFF8F9] p-3">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-medium text-sm">{p.name}</span>
                  <Badge className={SEVERITY_CLS[p.severity] || SEVERITY_CLS.low} variant="secondary">
                    {p.severity}
                  </Badge>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300">{p.evidence}</p>
              </div>
            ))}
          </div>
        )}

        {analysis.suggestedReply && (
          <div className="rounded-2xl border border-[#D9DEFF] bg-[#EEF0FF] p-4">
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-semibold text-sm flex items-center gap-2">
                <Lightbulb className="h-4 w-4" /> {t("flags.replyTitle")}
              </h4>
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(analysis.suggestedReply);
                  toast({ title: t("flags.copiedTitle"), description: t("flags.copiedDescription") });
                }}
                className="text-gray-400 hover:text-primary"
                aria-label={t("flags.copyReply")}
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm italic">"{analysis.suggestedReply}"</p>
          </div>
        )}

        {onRehearse && analysis.inputText && (
          <Button
            className="w-full"
            disabled={rehearsePending}
            onClick={() => onRehearse(analysis.inputText)}
          >
            <MessageCircle className="mr-2 h-4 w-4" />
            {t("flagsx.rehearse")}
          </Button>
        )}

        {!onDelete && <RedFlagShareCard analysis={analysis} t={t} toast={toast} />}
      </CardContent>
    </Card>
  );
}

export function FlagAnalysisScreen() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [image, setImage] = useState(null); // { file, url }
  const [result, setResult] = useState(null);

  // Bridge: start a freeform session seeded with the checked conversation, then open it.
  const rehearseMutation = useMutation({
    mutationFn: (conversation) => {
      const situation = `${t("flagsx.rehearseContext")}\n\n${conversation}`;
      return startPracticeSession({ situation }, i18n.language);
    },
    onSuccess: (res) => navigate(`/practice/${res.data.id}`),
    onError: (err) =>
      toast({
        title: t("flags.errorTitle"),
        description: err?.response?.data?.error || t("common.tryAgain"),
        variant: "destructive",
      }),
  });
  const onRehearse = (conversation) => rehearseMutation.mutate(conversation);

  const { data: historyData } = useQuery({
    queryKey: ["flag-analyses"],
    queryFn: getFlagAnalyses,
  });

  const analyzeMutation = useMutation({
    mutationFn: () =>
      image
        ? analyzeConversationImage(image.file, i18n.language)
        : analyzeConversation(text, i18n.language),
    onMutate: () => {
      setResult(null);
    },
    onSuccess: (res) => {
      if (res.data?.safety) {
        setResult({ safetyReply: res.data.safety.reply });
        return;
      }
      setResult(res.data);
      setText("");
      if (image) {
        URL.revokeObjectURL(image.url);
        setImage(null);
      }
      queryClient.invalidateQueries({ queryKey: ["flag-analyses"] });
    },
    onError: (err) =>
      toast({
        title: t("flags.errorTitle"),
        description: err?.response?.data?.error || t("common.tryAgain"),
        variant: "destructive",
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFlagAnalysis,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["flag-analyses"] }),
  });

  const history = historyData?.data || [];
  const visibleHistory = history
    .filter((item) => item.id !== result?.id)
    .filter((item, index, items) => {
      if (!item.inputText) return true;
      return items.findIndex((other) => other.inputText === item.inputText) === index;
    });

  return (
    <AppLayout
      title={t("flags.title")}
      description={t("flags.description")}
      hideHeader
    >
      <div className="relative overflow-hidden rounded-[2.25rem] p-4 md:p-7" style={{ background: "radial-gradient(circle at 90% 2%, rgba(240,68,97,0.14), transparent 29%), linear-gradient(180deg, #FFF2F4 0%, #FFFFFF 48%, #F5F6FF 100%)" }}>
        <div className="relative mb-5 flex min-h-[160px] items-center overflow-hidden rounded-[2rem] p-6 text-white shadow-[0_20px_46px_rgba(169,35,69,0.2)] md:p-7" style={{ background: "linear-gradient(135deg, #A92345 0%, #F04461 58%, #FF7479 100%)" }}>
          <div className="absolute -right-16 -top-20 h-64 w-64 rounded-full bg-white/10" />
          <div className="relative flex h-16 w-16 -rotate-6 items-center justify-center rounded-2xl border border-white/25 bg-white/15"><ShieldAlert className="h-8 w-8" /></div>
          <div className="relative ml-5 flex-1"><p className="text-xs font-extrabold uppercase tracking-[0.15em] text-white/65">Tone scan</p><h1 className="mt-1 text-3xl font-extrabold">{t("flags.title")}</h1></div>
          <div className="relative flex items-center gap-2 rounded-2xl bg-white/15 px-4 py-3"><span className="h-2.5 w-2.5 rounded-full bg-white" /><strong className="text-xl">{visibleHistory.length}</strong><span className="text-xs font-bold text-white/65">checks</span></div>
        </div>

        <div className="relative grid gap-4 lg:grid-cols-[minmax(0,1.12fr)_minmax(320px,0.88fr)]">
          <div className="space-y-4">
            <Card className="rounded-[1.75rem] border-[#F4D8DE] bg-white/95 shadow-[0_16px_35px_rgba(169,35,69,0.08)]">
              <CardContent className="space-y-4 p-5 md:p-6">
                <div className="flex items-center justify-between gap-3"><div className="flex items-center gap-3"><div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#F04461] text-white"><MessageCircle className="h-5 w-5" /></div><div><h2 className="font-extrabold">{t("flags.inputTitle")}</h2><p className="text-xs text-muted-foreground">Private second opinion</p></div></div><details className="text-xs text-muted-foreground"><summary className="cursor-pointer font-bold">{t("flagsx.howTitle")}</summary><div className="absolute right-5 z-20 mt-2 w-80 rounded-2xl border bg-white p-4 shadow-xl"><ol className="space-y-2">{[1, 2, 3].map((n) => <li key={n} className="flex gap-2"><span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#F04461] text-[10px] font-bold text-white">{n}</span>{t(`flagsx.how${n}`)}</li>)}</ol></div></details></div>

              {image ? (
                <div className="relative">
                  <img
                    src={image.url}
                    alt="screenshot"
                    className="max-h-80 w-full rounded-2xl border border-[#F4D8DE] bg-muted object-contain"
                  />
                  <button
                    type="button"
                    onClick={() => { URL.revokeObjectURL(image.url); setImage(null); }}
                    className="absolute top-2 right-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
                    aria-label="Remove image"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <Textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={t("flags.placeholder")}
                  rows={8}
                  maxLength={8000}
                  className="resize-none rounded-2xl border-[#F4D8DE] bg-[#FFFBFC] p-4 text-base"
                />
              )}

              {!image && (
                <label className="flex cursor-pointer items-center justify-center gap-2 rounded-2xl border border-dashed border-[#F4C9D1] bg-[#FFF5F7] py-3 text-sm font-bold text-[#F04461] hover:bg-[#FFF0F3]">
                  <ImagePlus className="h-4 w-4" />
                  {t("flagsx.addScreenshot")}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setText("");
                        setImage({ file, url: URL.createObjectURL(file) });
                      }
                      e.target.value = "";
                    }}
                  />
                </label>
              )}

              <Button
                className="h-12 w-full rounded-2xl bg-[#F04461] text-white hover:bg-[#D93B56]"
                onClick={() => analyzeMutation.mutate()}
                disabled={analyzeMutation.isPending || (!image && text.trim().length < 10)}
              >
                <ShieldAlert className="mr-2 h-4 w-4" />
                {analyzeMutation.isPending ? t("flags.loading") : t("flags.analyze")}
              </Button>
              <p className="text-xs leading-relaxed text-gray-400">
                {t("flags.disclaimer")}
              </p>
              </CardContent>
            </Card>

          {result?.safetyReply && (
            <Card className="rounded-[1.75rem] border-amber-300 bg-amber-50">
              <CardContent className="pt-6">
                <p className="text-sm flex items-start gap-2 text-amber-900 dark:text-amber-100">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  {result.safetyReply}
                </p>
              </CardContent>
            </Card>
          )}

          {result && !result.safetyReply && (
            <AnalysisCard
              analysis={result}
              toast={toast}
              onRehearse={onRehearse}
              rehearsePending={rehearseMutation.isPending}
            />
          )}
          </div>

          <div className="space-y-4">
          <div className="flex items-center justify-between px-1"><h3 className="font-extrabold text-[#1E293B]">{t("flags.history.title")}</h3><span className="rounded-full bg-[#FFF0F3] px-2.5 py-1 text-xs font-bold text-[#F04461]">{visibleHistory.length}</span></div>
          {visibleHistory.length === 0 && (
            <div className="rounded-[1.75rem] border border-dashed border-[#F4D8DE] bg-white/70 p-8 text-center text-sm text-gray-400">
              {t("flags.history.empty")}
            </div>
          )}
          {visibleHistory.map((a) => (
            <AnalysisCard
              key={a.id}
              analysis={a}
              onDelete={deleteMutation.mutate}
              onRehearse={onRehearse}
              rehearsePending={rehearseMutation.isPending}
              toast={toast}
            />
          ))}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
